import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { OrderInspection, OrderInspectionForm, OrderInspectionSearchForm } from 'entities/order_inspection'

type OrderInspectionsResponse = PagingResponse & {
    results: OrderInspection[]
}

export function useFetchOrderInspectionsApi(
    searchForm?: Form<OrderInspectionSearchForm>
): IndexApiSet<OrderInspectionsResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'order_inspections/'
    const api = useIndexApi<OrderInspectionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm?.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function usePostOrderInspectionApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderInspectionForm>) => void } {
    const apiSet = usePostApi<BaseResponse, OrderInspectionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderInspectionForm>) => {
        const apiPath = `order_inspections/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export type OrderInspectForm = {
    staffId?: number
}

export function useInspectOrderInspectionApi(): ApiSet<BaseResponse> & { execute: (orderInspectionId: number, form: Form<OrderInspectForm>) => void } {
    const apiSet = usePostApi<BaseResponse, OrderInspectForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (order_inspectionId: number, form: Form<OrderInspectForm>) => {
        const apiPath = `order_inspections/${order_inspectionId}/inspect/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutOrderInspectionApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderInspectionForm>) => void } {
    const apiSet = usePutApi<BaseResponse, OrderInspectionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderInspectionForm>) => {
        const apiPath = `order_inspections/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutOrderInspectionsApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderInspectionForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, OrderInspectionForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderInspectionForm[]>) => {
        const apiPath = `order_inspections/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteOrderInspectionApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `order_inspections/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
